import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { listDomains, verifyDomain, type DomainView } from './api';

/**
 * Custom domains (context.md sections 78-83).
 *
 * The DNS record is the whole screen. A domain sits unverified because somebody has not published a
 * TXT record, and the one thing that unblocks them is seeing exactly what to paste — so it is shown
 * in full, copyable, beside the domain it belongs to rather than behind a detail view.
 */

const PHASE_TONE: Record<string, BadgeTone> = {
  SERVING: 'positive',
  ACTIVE: 'positive',
  PENDING_DNS: 'warning',
  PENDING_CERTIFICATE: 'warning',
  VERIFYING: 'info',
  FAILED: 'danger',
};

export function DomainsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const domains = useQuery({ queryKey: ['domains'], queryFn: listDomains });

  const verify = useMutation({
    mutationFn: (id: string) => verifyDomain(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['domains'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<DomainView>[] = [
    {
      key: 'hostname',
      header: t('domains.hostname'),
      render: (domain) => (
        <span className="cell-stack">
          <span className="strong">{domain.hostname}</span>
          {domain.isPrimary && <Badge tone="info">{t('domains.primary')}</Badge>}
        </span>
      ),
    },
    {
      key: 'phase',
      header: t('crm.field.status'),
      render: (domain) => (
        <span className="cell-stack">
          <Badge tone={PHASE_TONE[domain.phase] ?? 'neutral'}>
            {t(`domains.phase.${domain.phase}`)}
          </Badge>
          {domain.failureReason && (
            <span className="muted small text-down">{domain.failureReason}</span>
          )}
        </span>
      ),
    },
    {
      key: 'record',
      header: t('domains.record'),
      render: (domain) =>
        domain.verification ? (
          <span className="cell-stack">
            <code>{domain.verification.recordName}</code>
            <code className="small">{domain.verification.recordValue}</code>
          </span>
        ) : (
          <span className="muted">{t('domains.noRecordNeeded')}</span>
        ),
    },
    {
      key: 'serving',
      header: t('domains.serving'),
      secondary: true,
      render: (domain) =>
        domain.serving ? (
          <Badge tone="positive">{t('common.yes')}</Badge>
        ) : (
          <span className="muted">{t('common.no')}</span>
        ),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      render: (domain) => formatDate(domain.createdAt),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (domain) =>
        domain.serving ? null : (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={verify.isPending}
            onClick={() => verify.mutate(domain.id)}
          >
            {t('domains.verifyNow')}
          </button>
        ),
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.domains')} description={t('domains.description')} />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel>
        <DataTable
          tableKey="domains"
          rows={domains.data?.domains}
          columns={columns}
          rowKey={(domain) => domain.id}
          isLoading={domains.isLoading}
          error={domains.error}
          onRetry={() => void domains.refetch()}
          empty={{ title: t('domains.empty'), description: t('domains.emptyBody') }}
        />
      </Panel>
    </div>
  );
}
